import type { CanAccess } from './access';

/**
 * The host-services seam (#2165, ADR-0035 D3).
 *
 * The kit renders an admin console: it translates labels, formats money and
 * dates, asks the operator to confirm a destructive action, reports the result,
 * remembers a table's filters, and gates affordances on who the caller is. Not
 * one of those is the kit's to OWN — each is a property of the host app, and a
 * kit that imported the host's i18next instance, toast provider or auth context
 * would be a kit only this app can mount.
 *
 * So core declares the SHAPE of each capability here and nothing else. This
 * module is framework-free by construction (its only import is a sibling type),
 * which is what lets the package's root export stay React-free while the
 * provider that installs these lives behind "./react".
 *
 * WHAT BELONGS HERE: genuinely ambient services — shared by the whole tree,
 * near-constant, painful to thread by hand. Per-row and per-cell data travels
 * as props; putting it here would multiply context reads by rows x columns and
 * buy nothing.
 *
 * WHAT DOES NOT: anything shaped like ONE host's implementation. `confirm`
 * takes an `intent`, not the host dialog's `type`; `notify` takes a closed-map
 * kind, not four differently-named methods; `storage` takes a logical key, not
 * the host's physical `localStorage` key. Each seam is the contract, and the
 * adapter at the host is where the host's spelling is applied.
 */

/**
 * The i18n seam. Deliberately WIDER than `TranslateFn` in the model package
 * (`opts?: { defaultValue?: string }`): the kit interpolates `name`, `count`,
 * `limit`, `field` and `error` at 21 call sites, and a narrow options type
 * would reject every one of them on excess-property grounds. A wide function
 * is assignable to the narrow one, so the narrower CSV contract is unaffected.
 */
export type Translate = (key: string, opts?: Record<string, unknown>) => string;

/**
 * The two i18n facts that are NOT a translation: which locale is active (print
 * jobs stamp it on the rendered sheet) and whether a key is defined (error
 * resolution probes for a feature-local sentinel key before falling back).
 *
 * A separate member rather than properties hung on `t`, because a function
 * carrying data is not a contract anyone can implement twice; and a probe
 * rather than the i18next instance itself, because naming `i18next` in core
 * would make the package depend on the very thing this seam exists to avoid.
 */
export interface I18nProbe {
  readonly language: string;
  exists(key: string): boolean;
}

/**
 * The key `ResourceKitProvider` probes at install time to prove a host merged
 * the kit's own catalog (`@resource-kit/ui/locales/{en,zh}.json`) into the
 * i18n instance behind `I18nProbe`.
 *
 * ONE key, not the catalog. Core cannot see the catalog — `@resource-kit/ui`
 * is downstream of it in the package chain (ADR-0035 D1), and importing the
 * locale files here to check them all would invert the dependency the package
 * split exists to hold. One key answers the only question the install-time
 * check asks: was the merge done AT ALL. Whether every key is present is a
 * different question, answered at test time by the host's
 * `i18n-source-coverage` guard.
 *
 * `rk.ui.loading` is the choice because it is the key the kit's two most basic
 * primitives read — both exported from `@resource-kit/ui`'s
 * `react/primitives/Loading.tsx`: `Loading` uses it as the spinner's
 * `aria-label` (`packages/resource-kit-ui/react/primitives/Loading.tsx::Loading`),
 * and `LoadingPage`, a SEPARATE export rather than a mode of the first, falls
 * back to it for the caption under that spinner when the caller passes no
 * `message` (`packages/resource-kit-ui/react/primitives/Loading.tsx::LoadingPage`)
 * — so
 * it is as load-bearing a row as the catalog has. Both are spelled in full
 * (path AND symbol) rather than as a bare name, since the two citations point
 * at a different file than this one and a bare symbol would read as a
 * SAME-FILE reference.
 * NOTHING IN THE RUNTIME ENFORCES THE PAIRING, though: `t()` answers a dropped
 * key with the key itself, so both primitives would keep rendering, with
 * `rk.ui.loading` in the label. The enforcement is a test-time one, below.
 *
 * A CONSTANT IN CORE POINTING AT A ROW OF A DOWNSTREAM CATALOG is a link no
 * compiler can hold, so a host has to pin it in its own suite; this repository
 * does so in `frontend/src/__tests__/architecture/i18n-key-parity.test.ts`.
 * That row is the only thing standing between a catalog rename and a stale
 * constant here, which would make every correctly-wired host fail hard, on the
 * very check that exists to tell correct hosts from broken ones.
 */
export const KIT_CATALOG_CANARY_KEY = 'rk.ui.loading' as const;

/**
 * Locale- and timezone-aware formatting. The kit never hardcodes a locale and
 * never computes a merchant timezone: both are host facts, and a display that
 * silently used the browser's locale would be wrong in a way no test notices.
 *
 * The member names mirror the host helpers one-for-one on purpose — an adapter
 * that has to rename six functions is an adapter that can transpose two of them
 * and still typecheck.
 */
export interface Formatters {
  formatDate(value: string | number | Date | null | undefined): string;
  formatDateTimeCompact(value: string | number | Date | null | undefined): string;
  formatDatetimeSince(value?: string | null): string;
  formatDuration(milliseconds: number): string;
  formatCurrency(amount: number, currency?: string): string;
  formatNumber(value: number, options?: Intl.NumberFormatOptions): string;
  /**
   * The operating zone as an IANA name (`America/Los_Angeles`), which is the
   * ONE timezone fact the kit reads rather than formats.
   *
   * `@resource-kit/controllers`' `serialize` turns a filter bound the operator
   * typed as a wall clock into the UTC instant the backend compares against,
   * and it takes the zone as a PARAMETER — a serializer that read the host's
   * zone itself would drag a host module into a framework-free package
   * (ADR-0035 D4, the `lib/merchant-tz.ts` row of
   * `docs/adr/0035-resource-kit-independence.md`). The hooks
   * that call it are the ones that need somewhere to
   * read the zone FROM, and this is it.
   *
   * A value, not a function, because it is a fact rather than a projection: the
   * six members above answer "render this value", and this one answers "in
   * whose calendar". An adapter is free to implement it as a getter, and the
   * install-time check
   * (`packages/resource-kit-core/ResourceKitProvider.tsx::ResourceKitProvider`) is
   * `== null`, which a
   * getter satisfies by returning a string.
   */
  readonly timeZone: string;
}

/**
 * Result-branch feedback kinds. `as const` map -> derived union so a kind can
 * never be a magic string at a call site.
 *
 * All FOUR kinds the host's toast can render are here on purpose. A seam that
 * offers three of the four is not a translation of the capability, it is a
 * narrowing of it: a kit call site with a genuinely non-fatal warning has no
 * member to route it to and is pushed into calling it an `error` (alarming) or
 * `info` (invisible), and the host's fourth method becomes unreachable from
 * every kit surface.
 */
export const NOTIFY_KIND = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
} as const;

export type NotifyKind = (typeof NOTIFY_KIND)[keyof typeof NOTIFY_KIND];

/**
 * Transient feedback. ONE function with the kind as its first argument, not
 * three methods: every kit call site already routes a computed kind, and a
 * three-method object would force each of them to re-derive the dispatch.
 */
export type Notify = (kind: NotifyKind, title: string, message?: string) => void;

/**
 * How serious a confirmation is — the kit's word, not the host dialog's. The
 * host maps it onto whatever its own dialog calls the same idea.
 *
 * Same rule as `NOTIFY_KIND` above: the seam mirrors the host capability it
 * replaced rather than a subset of it, so `info` is here even though no kit
 * call site asks for it yet. Dropping it would mean the only way to raise a
 * purely informational confirmation is to dress it as a `default` one.
 */
export const CONFIRM_INTENT = {
  default: 'default',
  info: 'info',
  warning: 'warning',
  danger: 'danger',
} as const;

export type ConfirmIntent = (typeof CONFIRM_INTENT)[keyof typeof CONFIRM_INTENT];

export interface ConfirmRequest {
  title: string;
  message: string;
  intent?: ConfirmIntent;
  confirmText?: string;
  cancelText?: string;
}

/** Ask the operator. Resolves `true` only on an explicit confirmation. */
export type Confirm = (request: ConfirmRequest) => Promise<boolean>;

/**
 * Durable per-caller preferences (a list's last filters, a table's column
 * visibility). Keys are LOGICAL — the host owns namespacing, scoping to the
 * signed-in user, versioning and the storage medium.
 *
 * Values are `unknown` in BOTH directions, and validating a read is the
 * caller's job. A seam that narrowed to `string` would force every
 * object-valued consumer through a second JSON encode and would make an
 * already-stored object read as absent — silently discarding preferences the
 * operator set. The seam moves bytes; only the consumer knows what they mean.
 *
 * BOTH SHAPES ARE ON THIS SEAM SINCE #2169 PR-2. `useDataTableParams` stores a
 * query string; `@resource-kit/ui`'s `useColumnVisibility` stores the
 * column-visibility map that motivates `unknown` — it used to reach
 * `lib/store`'s `createStore` directly from the host. `unknown` was chosen back
 * in #2165, before the second consumer existed, because widening a seam after
 * consumers have written under it is the change that cannot be made compatibly.
 */
export interface PreferenceStore {
  read(key: string): unknown;
  write(key: string, value: unknown): void;
  remove(key: string): void;
  /**
   * Is there an entry under this key at all? (#2183)
   *
   * ABSENT vs STORED NULL — the one thing `read` cannot say. `read` answers
   * `unknown`, so `null` comes back both for a key nobody ever wrote and for a
   * key holding a stored `null`: the host store installs no sanitizer, so that
   * value travels back intact and only the consumer can call it unusable. The
   * two want OPPOSITE handling — an entry that exists and cannot be used is
   * corrupt and should be discarded; an absent key must not turn a fresh mount
   * into a write. Only the store knows which one it is, so it says so here
   * rather than leaving every consumer to guess from the value.
   *
   * Whether a stored `null` sits in any operator's browser is UNKNOWN, not
   * ruled out: no writer through this seam produces one — `useDataTableParams`
   * writes a non-empty query string, `useColumnVisibility` a visibility map —
   * but writers from earlier revisions were not audited.
   *
   * A STORE'S OWN REJECTIONS ARE NOT IN SCOPE: the store removes what it
   * refuses before its `read` answers, so `has` reports it absent — except
   * when that removal throws, which leaves the entry in place, `read`
   * answering `null` and `has` answering true, for the consumer's `remove` to
   * retry.
   *
   * ORDERING CONTRACT: call `has` only after a `read` of the same key in the
   * same task. `read` may discard a stored entry it rejects, so a consumer
   * that calls `has` first can observe `true`, then have `read` remove the
   * entry, then remove it a second time on the strength of that stale `true`.
   */
  has(key: string): boolean;
}

/**
 * Identity facts a schema's tab gate asks about (#1917 / #1964).
 *
 * BOTH members survive, and a gate that reads only one of them is usually
 * wrong: the backend's `requirePermission` short-circuits on the admin role, so
 * a UI gate checking the grant alone would hide a tab the API would have served
 * to that same admin.
 */
export interface ResourceTabGateContext {
  isAdmin: boolean;
  /** Does the caller hold this permission key? (Grants only — see above.) */
  hasGrant: (permission: string) => boolean;
}

/** Everything the kit needs from its host, installed once at the app's root. */
export interface ResourceKitServices {
  canAccess: CanAccess;
  t: Translate;
  i18n: I18nProbe;
  format: Formatters;
  notify: Notify;
  confirm: Confirm;
  storage: PreferenceStore;
  access: ResourceTabGateContext;
}

const SERVICE_KEYS = [
  'canAccess',
  't',
  'i18n',
  'format',
  'notify',
  'confirm',
  'storage',
  'access',
] as const satisfies readonly (keyof ResourceKitServices)[];

/**
 * The keys the provider verifies at install time.
 *
 * `satisfies` on the tuple rejects a key that names no service; the annotation
 * on the export rejects the opposite mistake — a service ADDED to
 * `ResourceKitServices` and forgotten here. The condition reads "is there any
 * interface key this tuple does not list?", and if there is, the declared type
 * collapses to `never` and the initialiser below stops assigning, naming the
 * missing key in the error. (The `[…] extends […]` tuple wrapper is what stops
 * `Exclude` from distributing, which would make a union of misses read as a
 * match.)
 *
 * This replaces a comment that claimed "nothing in the type system can" catch
 * it and pointed at the seam's `toEqual([...])` test instead. That test asserts
 * this list against a hand-written literal, so it goes green for a ninth
 * interface member exactly as readily as this list does — it pins the list, not
 * the list's completeness.
 */
export const REQUIRED_SERVICE_KEYS: [
  Exclude<keyof ResourceKitServices, (typeof SERVICE_KEYS)[number]>,
] extends [never]
  ? typeof SERVICE_KEYS
  : never = SERVICE_KEYS;

/**
 * The services that are OBJECTS, and therefore assemblable in halves — DERIVED
 * from the interface, never listed.
 *
 * A hand-written union here had no type relation to `ResourceKitServices` at
 * all: the member manifest below is type-enforced for members INSIDE a listed
 * composite, but WHICH composites are listed was maintained by hand, so a ninth
 * object-valued service could be added to the interface and to `SERVICE_KEYS`,
 * compile clean, and get zero member coverage — the exact hole one level up
 * from the one the manifest exists to close.
 *
 * The mapped type asks each key "is this a function?" and keeps the ones that
 * are not: `t`, `notify`, `confirm` and `canAccess` are call signatures and
 * drop out; `i18n`, `format`, `storage` and `access` are object shapes and stay
 * in. Nothing to keep in sync — adding an object-valued service is what makes
 * `REQUIRED_SERVICE_MEMBERS` demand a manifest entry for it.
 */
export type CompositeServiceKey = {
  [K in keyof ResourceKitServices]: ResourceKitServices[K] extends (...args: never[]) => unknown
    ? never
    : K;
}[keyof ResourceKitServices];

const SERVICE_MEMBERS = {
  i18n: ['language', 'exists'],
  format: [
    'formatDate',
    'formatDateTimeCompact',
    'formatDatetimeSince',
    'formatDuration',
    'formatCurrency',
    'formatNumber',
    'timeZone',
  ],
  storage: ['read', 'write', 'remove', 'has'],
  access: ['isAdmin', 'hasGrant'],
} as const;

/**
 * What each composite service must actually contain, verified at install time
 * alongside the keys above.
 *
 * A `!= null` check on `format` is satisfied by `{}`, which then throws at the
 * first cell that formats a date — the same "crashes at a tab nobody opens
 * until a fortnight later" failure the whole install-time check exists to
 * delete, arriving one level down. The keys listed are exactly the ones with
 * members, and that set is `CompositeServiceKey` — derived, not typed out — so
 * this manifest cannot fall behind the interface: an object-valued service
 * added there makes the mapped type below demand an entry here by name.
 *
 * Same two-sided typing as `REQUIRED_SERVICE_KEYS`: the annotation's element
 * type rejects a member name that does not exist on the service, and the
 * per-key `never` collapse rejects a member added to the interface and
 * forgotten here.
 */
export const REQUIRED_SERVICE_MEMBERS: {
  readonly [K in CompositeServiceKey]: [
    Exclude<keyof ResourceKitServices[K], (typeof SERVICE_MEMBERS)[K][number]>,
  ] extends [never]
    ? readonly (keyof ResourceKitServices[K])[]
    : never;
} = SERVICE_MEMBERS;
